import * as React from "react";
import { KamstrupLicenses, useAvailableLicenses } from "src/api";

export interface FeatureToggleProps {
  licenses: KamstrupLicenses[];
  method?: "AND" | "OR";
  disabled?: React.ReactNode;
  loading?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Renders children based on available licenses for the user. The component will render children if the license check passes.
 *
 * uses the useCheckLicenses hook.
 *
 * @param licenses - Array of licenses to check.
 * @param method - (Optional) Method used for license check.
 *                - "AND" (default) will check if user has all provided licenses.
 *                - "OR" will check if user has at least one of the provided licenses
 * @param loading - (Optional) Fallback component during license check resolution.
 * @param disabled - (Optional) Fallback component when the license check fails.
 * @param children - (Optional) React children to render when the license check passes.
 */
export const LicenseGuard: React.FunctionComponent<FeatureToggleProps> = ({
  licenses,
  method,
  disabled,
  loading,
  children,
}) => {
  const { data, isLoading } = useAvailableLicenses();

  if (isLoading) {
    return <>{loading}</>;
  }

  if (!data) {
    return <></>;
  }

  const passed = checkLicenses(data?.licensesAvailable, licenses, method || "AND");

  if (passed) {
    return <>{children}</>;
  }

  return <>{disabled}</>;
};

export const checkLicenses = (
  availableLicenses: KamstrupLicenses[],
  licensesToCheck: KamstrupLicenses[],
  method: "AND" | "OR"
): boolean => {
  switch (method) {
    case "OR":
      return licensesToCheck.some((l) => availableLicenses.includes(l));
    default: // AND case
      return licensesToCheck.every((l) => availableLicenses.includes(l));
  }
};
